import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';
import { UserData } from '../_models/user-data.model';


import { firstValueFrom } from 'rxjs';

import { GlobalVariable } from '../shared/global.settings';
import { MessageService } from './message.service';
import { LogService } from './logging/log.service';

const USER_STORAGE_KEY = 'user_token';

export interface IJwtPayload {
  iat: string;
  iss: string;
  exp: string;
  data: {
    application?: string;
    email?: string;
    name?: string;
    roles?: string;
    username?: string;
    expiresAt?: string;
  }
}

/**
 * @service AuthService
 * @memberof Services
 * @classdesc The AuthService class is used to interact with the authentication API
 
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #loginURL = GlobalVariable.AUTH_BASE + '/login';

  http = inject(HttpClient);
  messageService = inject(MessageService);
  logService = inject(LogService);

  #id_token = signal<string>('');

  #userSignal = signal<UserData | null>(null);
  user = this.#userSignal.asReadonly();

  isLoggedIn = computed(() => {
    const user = this.user();
    if (user) {
      return this.isTokenValid(user.jwt);
    } else {
      return false;
    }
  });

  isLoggedOut = computed(() => !this.isLoggedIn());

  /**
   * @constructor
   * 
   */
  constructor() {
    this.loadUserFromStorage();
    effect(() => {
      const user = this.user();
      if (user) {
        sessionStorage.setItem(USER_STORAGE_KEY,
          JSON.stringify(user));
      }
    });
  }

  /**
   * @function getUser
   * @memberof AuthService
   * @description Routine that returns the user object
   * @returns UserData object containing the user details
   */

  loadUserFromStorage() {
    const json = sessionStorage.getItem(USER_STORAGE_KEY);
    if (json) {
      const user = JSON.parse(json);

      this.#userSignal.set(user);
    }
  }

  /**
   * @function login
   * @memberof AuthService
   * @description Routine that calls the backend to login a user
   * @param username of the user to be logged in
   * @param password of the user to be logged in
   * @returns UserData object containing the user details or null if login failed
   */

  async login(username: string, password: string): Promise<UserData | null> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    let options = { headers: headers };

    // Build our JSON post request body and get JWT
    let loginData = {
      appName: GlobalVariable.APPLICATION_NAME,
      user_email: username,
      password: password
    };
    let body = JSON.stringify(loginData);
    const login$ = this.http.post(this.#loginURL, body, options);
    const res = await firstValueFrom(login$)
      .catch((err) => {
        this.logService.error('Login failed', err);
        this.messageService.showMessage('Login failed: ' + err, 'error');
        return null;
      }) as { jwt: string } | null;
    if (!res) {  // Login failed, return null
      this.logService.error('Login failed', 'No token returned from server');
      this.messageService.showMessage('Login failed: No token returned from server', 'error');
      return null;
    }
    // Have Token so setup UserData
    if (this.isTokenValid(res.jwt)) {
      const jwt = <IJwtPayload>jwtDecode(res.jwt);

      this.#userSignal.set({
        email: jwt.data.email || '',
        username: jwt.data.username || '',
        name: jwt.data.name || '',
        application: jwt.data.application || GlobalVariable.APPLICATION_NAME,
        roles: jwt.data.roles || '',
        jwt: res.jwt || '',
      })
      this.#id_token.set(res.jwt);
      //this.messageService.showMessage('Login Successful', 'success');
    }

    return null
  }

  /**
   * @function authtoken
   * @memberof AuthService
   * @description Routine that returns the JWT token for the user
   * @returns JWT token for the user
   */
  authtoken(): string {
    return this.#id_token();
  }


  /**
   * @function logout
   * @memberof AuthService
   * @description Routine that logs out the user by removing the token from session storage and setting the user to null
   * @returns void
   */
  async logout(): Promise<void> {
    sessionStorage.removeItem(USER_STORAGE_KEY);
    this.#userSignal.set(null);
  }

  /**
   * @function getUserName
   * @memberof AuthService
   * @description Routine that returns the username of the user
   * @returns username of the user
   */
  public getUserName(): string {
    if (this.#userSignal()) {
      return this.#userSignal()!.username;
    } else {
      return '';
    }
    ;
  }

  /**
   * @function getUserRoles
   * @memberof AuthService
   * @description Routine that returns the roles of the user
   * @returns roles of the user
   * 
   */
  getUserRoles(): string[] {
    if (this.#userSignal()) {
      const roles = this.#userSignal()!.roles;
      return roles ? roles.split(',') : [];
    }
    return [];
  }



  /**
   * @function isTokenValid
   * @memberof AuthService
   * @description Routine that checks if the token is valid and has not expired
   * @param jwt token to be checked 
   * @returns true if the token is valid and has not expired, false otherwise
   */

  public isTokenValid(jwt: string): boolean {
    if (jwt) {
      const decodedToken = <IJwtPayload>jwtDecode(jwt);
      const expiresAt = new Date(parseInt(decodedToken.exp))
      return expiresAt
        ? expiresAt.getTime() > Date.now()
        : false;

    } else {
      return false;
    }
  }
}

